"use client";

import { expansionClick, tabSwitched } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Elective 1065 — KUK Electives.
 *
 * Port of kuk-campus-portal `components/Degree/Electives.tsx`: the elective names as a
 * tab list — a two-column grid below 768px, a stacked rail with a caret on the active
 * row above it — beside a shadowed card holding that elective's subjects and the
 * semester each falls in.
 *
 * DATA — the existing three-level elective store, which already has this shape:
 *   elective_category  = one elective ("Business Analytics")  → the tab
 *   elective_semester  = a semester within it ("3rd Sem")     → the right-hand column
 *   elective_subject   = a subject                            → a table row
 * v1 receives one flat `elective_courses[]` per elective and re-derives the semester
 * grouping by sorting on `semester` and watching for the value to change; storing the
 * semester as the real parent removes that derivation and the blank-row bookkeeping
 * that goes with it.
 *
 * THE SPACER ROW IS REAL. v1 emits an empty `<tr>` whenever the semester changes
 * between two adjacent rows, so the table reads as grouped blocks rather than one run.
 * Reproduced — as a gap before each group after the first, which is the same rendered
 * result and cannot desynchronise from the grouping.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see globals.css
 * `--breakpoint-*`), so every `md:` in the reference is ported as `tab:`.
 *
 * TAB LABELS TRUNCATE BELOW 768px in the reference — it prints only the first two
 * words of the elective name on a phone, at `text-xs`. Kept, because the two-column
 * grid it sits in has no room for the full name.
 *
 * COLUMN LABELS ARE FIXED, as they are in the reference: "Subject" and "Semester" are
 * literals in v1's markup, not values it receives, so they stay literals here rather
 * than becoming two columns nobody would ever change. Everything the reference does
 * vary — the elective names, their semesters, the subjects and the tab colours — is
 * already stored.
 *
 * A NOTE ON `credits`. `elective_subject.credits` is stored and not drawn, because the
 * reference's table has no credits column; the field is left alone so a later variant
 * can show it without a migration.
 */

/** Fixed in the reference's markup. */
const SUBJECT_COLUMN_LABEL = "Subject";

/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used on
 * 12 headings across the reference. Defined in ITS globals.css as a `::after` with
 * `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can come
 * from a CMS field (a pseudo-element cannot read an inline style). `w-fit` on the
 * heading is load-bearing: the rule is 33% of the HEADING, and a full-width h2 would
 * stretch it across a third of the page.
 */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

/** v1 paints every `.custom-underline` this one colour; `elective` has no spare
 * colour column to expose it through, so it stays the reference's constant. */
const HEADING_ACCENT = "#9F1C33";

const SEMESTER_COLUMN_LABEL = "Semester";

/** The 16x16 caret the reference renders on the active tab from 768px up. */
const CaretRight = ({ color }: { color: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M9 6L15 12L9 18"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function isExplicitlyOff(v: number | boolean | undefined | null): boolean {
  return v === 0 || v === false;
}

interface ElectiveSubject {
  id?: number;
  subject_name: string;
  subject_name_text_color?: string;
  credits?: number;
  priority?: number;
  status?: number | boolean;
}

interface ElectiveSemester {
  id?: number;
  name: string;
  name_color?: string;
  order_index?: number;
  is_active?: number | boolean;
  status?: number | boolean;
  subjects?: ElectiveSubject[];
}

interface ElectiveCategory {
  id?: number;
  name: string;
  name_text_color?: string;
  /** Inactive tab fill. */
  section_color?: string;
  /** Active tab fill. */
  selected_section_color?: string;
  /** Active tab rule + caret. */
  selected_section_strip_color?: string;
  /** Subject card surface. */
  subject_container_color?: string;
  /** Table header rule. */
  header_section_color?: string;
  order_index?: number;
  is_active?: number | boolean;
  status?: number | boolean;
  semesters?: ElectiveSemester[];
}

type ElectiveSection = {
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  background_color?: string;
  categories?: ElectiveCategory[];
  status?: boolean | number;
};

interface Elective1065Props {
  data?: { data: ElectiveSection } | ElectiveSection;
  className?: string;
}

/** The elective payload arrives at one, two or three levels of `data` nesting. */
function getSection(data: Elective1065Props["data"]): ElectiveSection {
  if (!data || typeof data !== "object") return {};
  const root = data as Record<string, unknown>;
  if (Array.isArray(root.categories)) return data as ElectiveSection;
  const once = root.data;
  if (once && typeof once === "object") {
    const inner = once as Record<string, unknown>;
    if (Array.isArray(inner.categories) || inner.title != null) {
      return once as ElectiveSection;
    }
    const twice = inner.data;
    if (twice && typeof twice === "object") return twice as ElectiveSection;
  }
  return (once as ElectiveSection) ?? {};
}

const Elective1065 = ({ data, className }: Elective1065Props) => {
  const previousElectiveRef = useRef<string>("");

  const {
    title = "Electives",
    title_text_color = "#101112",
    sub_title = "",
    sub_title_text_color = "#4B4B4B",
    background_color = "#FFFFFF",
    categories = [],
    status,
  } = getSection(data);

  const electives = useMemo(
    () =>
      categories
        .filter((c) => !isExplicitlyOff(c.is_active) && !isExplicitlyOff(c.status))
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
    [categories],
  );

  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (electives.length === 0) return;
    setSelected((prev) => {
      if (prev && electives.some((e) => e.name === prev)) return prev;
      const first = electives[0].name;
      if (previousElectiveRef.current === "") previousElectiveRef.current = first;
      return first;
    });
  }, [electives]);

  const handleSelect = useCallback((name: string) => {
    if (previousElectiveRef.current !== name && previousElectiveRef.current !== "") {
      tabSwitched({ section: "electives", component: name, label: name });
      expansionClick({ section: "electives", component: name, label: name });
    }
    previousElectiveRef.current = name;
    setSelected(name);
  }, []);

  const current = electives.find((e) => e.name === selected) || electives[0];

  const semesters = useMemo(() => {
    if (!current?.semesters) return [] as ElectiveSemester[];
    return current.semesters
      .filter((s) => !isExplicitlyOff(s.is_active) && !isExplicitlyOff(s.status))
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  }, [current]);

  if (isExplicitlyOff(status) || electives.length === 0) return null;

  return (
    <section
      className={cn("w-full p-5 py-10 tab:px-20 tab:py-10", className)}
      style={{ backgroundColor: background_color }}
    >
      <div className="mx-auto w-full max-w-[1120px]">
        {title ? (
          <h2
            className="relative w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_text_color }}
          >
            {title}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: HEADING_ACCENT }}
            />
          </h2>
        ) : null}
        {sub_title ? (
          <p className="mt-2 text-sm" style={{ color: sub_title_text_color }}>
            {sub_title}
          </p>
        ) : null}

        <div
          className={cn(
            "grid h-full w-full grid-cols-1 items-start justify-between",
            "tab:grid-cols-2 tab:min-h-[320px]",
          )}
        >
          {/* ── tab list ──────────────────────────────────────────────────── */}
          <div
            className={cn(
              "mb-10 mt-14 grid h-auto w-full grid-cols-2 items-center justify-between",
              "tab:flex tab:flex-col tab:items-start tab:justify-start",
            )}
          >
            {electives.map((elective) => {
              const active = elective.name === current?.name;
              const stripColor = elective.selected_section_strip_color || "#9F1C33";
              return (
                <button
                  key={elective.id ?? elective.name}
                  type="button"
                  onClick={() => handleSelect(elective.name)}
                  aria-pressed={active}
                  className={cn(
                    "relative w-full rounded-none py-3 text-center transition-colors",
                    "tab:w-[330px] tab:text-left tab:pl-4",
                    active
                      ? "border-b-2 font-bold tab:border-b-0 tab:border-l-2"
                      : "border-b-2 border-transparent tab:border-b-0 tab:border-l-2",
                  )}
                  style={{
                    backgroundColor: active
                      ? elective.selected_section_color || "#FFFFFF"
                      : elective.section_color || "rgba(159, 28, 51, 0.13)",
                    color: active
                      ? stripColor
                      : elective.name_text_color || "#101112",
                    borderColor: active ? stripColor : "transparent",
                  }}
                >
                  <span className="hidden tab:block">{elective.name}</span>
                  <span className="text-xs tab:hidden">
                    {elective.name.split(" ").slice(0, 2).join(" ")}
                  </span>
                  <span className="absolute inset-y-0 right-2 hidden items-center justify-center tab:flex">
                    <CaretRight color={active ? stripColor : "#000000"} />
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── subject card ──────────────────────────────────────────────── */}
          <div
            className="relative rounded-xl px-7 py-7 shadow-2xl"
            style={{
              backgroundColor: current?.subject_container_color || "#FFFFFF",
              color: title_text_color,
            }}
          >
            <table className="w-full text-left">
              <thead>
                <tr
                  className="border-b-2"
                  style={{
                    borderColor: `${current?.header_section_color || "#101112"}33`,
                  }}
                >
                  <th className="w-[100px] pb-2 text-sm font-medium">
                    {SUBJECT_COLUMN_LABEL}
                  </th>
                  <th className="pb-2 text-right text-sm font-medium">
                    {SEMESTER_COLUMN_LABEL}
                  </th>
                </tr>
              </thead>
              <tbody>
                {semesters.map((semester, si) => {
                  const subjects = (semester.subjects || [])
                    .filter((s) => !isExplicitlyOff(s.status))
                    .sort((a, b) => (a.priority || 0) - (b.priority || 0));
                  return subjects.map((subject, i) => (
                    <tr
                      key={subject.id ?? `${semester.name}-${i}`}
                      // v1's blank separator row between semester groups.
                      className={cn(i === 0 && si > 0 && "[&>td]:pt-6")}
                    >
                      <td
                        className="w-full py-1 text-sm font-light"
                        style={{ color: subject.subject_name_text_color || undefined }}
                      >
                        {subject.subject_name}
                      </td>
                      <td
                        className="whitespace-nowrap py-1 text-right text-sm"
                        style={{ color: semester.name_color || undefined }}
                      >
                        {semester.name}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Elective1065;
